import api from '../api';
import { getCurrentLanguage, normalizeLanguage } from '../i18n';

export const VOICE_INTENTS = Object.freeze({
  MAP: 'MAP',
  UI_ACTION: 'UI_ACTION',
  SELECT_TARGET: 'SELECT_TARGET',
  READ_OUT_LOUD: 'READ_OUT_LOUD',
  SEND_VOICE_MESSAGE: 'SEND_VOICE_MESSAGE',
  UNKNOWN: 'UNKNOWN'
});

const MAX_VOICE_MESSAGE_LENGTH = 1200;
const MAX_TTS_TEXT_LENGTH = 5000;

export const normalizeVoiceText = (value = '') => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[\u00bf?\u00a1!.,;:]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

export const sanitizeVoicePayload = (value = '', maxLength = MAX_VOICE_MESSAGE_LENGTH) => String(value)
  // eslint-disable-next-line no-control-regex
  .replace(/[\x00-\x1F\x7F]/g, ' ')
  .replace(/<\/?script[^>]*>/gi, '')
  .replace(/\s+/g, ' ')
  .trim()
  .slice(0, maxLength);

const matchFirst = (text, patterns) => {
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) return match;
  }
  return null;
};

export const VOICE_COMMAND_PATTERNS = [
  {
    action: { intent: VOICE_INTENTS.MAP, target: 'tasks', path: '/tareas' },
    patterns: {
      es: [/^(?:ir|ve|navega|abrir|abre|mostrar|muestra)\s+(?:a\s+)?(?:mis\s+)?tareas$/],
      en: [/^(?:go to|open|show)\s+(?:my\s+|the\s+)?tasks$/]
    }
  },
  {
    action: { intent: VOICE_INTENTS.MAP, target: 'places', path: '/lugares' },
    patterns: {
      es: [/^(?:ir|ve|navega|abrir|abre|mostrar|muestra)\s+(?:a\s+)?(?:mis\s+)?lugares$/],
      en: [/^(?:go to|open|show)\s+(?:my\s+|the\s+)?places$/]
    }
  },
  {
    action: { intent: VOICE_INTENTS.MAP, target: 'network', path: '/network' },
    patterns: {
      es: [
        /^(?:ir|ve|navega|cambia|cambiar|abrir|abre|mostrar|muestra)\s+(?:a\s+)?(?:la\s+)?red(?:\s+q)?$/,
        /^(?:cambiar|cambia)\s+a\s+conexiones$/
      ],
      en: [/^(?:go to|open|show)\s+(?:the\s+)?(?:q\s+)?network$/]
    }
  },
  {
    action: { intent: VOICE_INTENTS.UI_ACTION, action: 'TOGGLE_GROUPS_BAR', target: 'groups', path: '/network' },
    patterns: {
      es: [
        /^(?:abrir|abre|mostrar|muestra|ir|ve)\s+(?:a\s+)?grupos$/,
        /^(?:desplegar|despliega|abrir|abre|mostrar|muestra)\s+(?:la\s+)?barra(?:\s+de\s+(?:redes|grupos))?$/
      ],
      en: [/^(?:go to|open|show)\s+(?:the\s+)?groups$/]
    }
  },
  {
    action: { intent: VOICE_INTENTS.MAP, target: 'qoph', path: '/qoph' },
    patterns: {
      es: [
        /^(?:ir|ve|navega|cambia|cambiar|abrir|abre)\s+(?:a\s+|al\s+)?(?:qoph|oraculo(?:\s+qoph)?)$/,
        /^(?:llamar|llama|invocar|invoca)\s+a\s+qoph$/
      ],
      en: [/^(?:go to|open|show|call|invoke)\s+(?:qoph|oracle)$/]
    }
  },
  {
    action: { intent: VOICE_INTENTS.MAP, target: 'subconsciousness', path: '/subconciencia' },
    patterns: {
      es: [
        /^(?:ir|ve|navega|cambia|cambiar|abrir|abre|abri|mostrar|muestra)\s+(?:a\s+|la\s+)?(?:subconciencia|subconsciente|suenos|sueno)$/,
        /^(?:entrar|entra)\s+(?:a\s+|al\s+)?(?:sueno|subconsciente)$/
      ],
      en: [
        /^(?:go to|open|show|enter)\s+(?:the\s+)?(?:subconscious|subconsciousness|dream|dreams)$/,
        /^open\s+subconscious$/
      ]
    }
  },
  {
    action: { intent: VOICE_INTENTS.MAP, target: 'settings', path: '/ajustes' },
    patterns: {
      es: [/^(?:ir|ve|navega|abrir|abre|mostrar|muestra)\s+(?:a\s+)?(?:ajustes|configuracion|preferencias)$/],
      en: [/^(?:go to|open|show)\s+(?:settings|preferences)$/]
    }
  },
  {
    action: { intent: VOICE_INTENTS.MAP, target: 'profile', path: '/profile' },
    patterns: {
      es: [/^(?:ir|ve|navega|abrir|abre|mostrar|muestra)\s+(?:a\s+)?(?:mi\s+)?perfil$/],
      en: [/^(?:go to|open|show)\s+(?:my\s+)?profile$/]
    }
  },
  {
    action: { intent: VOICE_INTENTS.MAP, target: 'gallery', path: '/profile/gallery' },
    patterns: {
      es: [/^(?:ir|ve|navega|abrir|abre|mostrar|muestra)\s+(?:a\s+)?(?:la\s+)?galeria$/],
      en: [/^(?:go to|open|show)\s+(?:the\s+)?gallery$/]
    }
  },
  {
    action: { intent: VOICE_INTENTS.MAP, target: 'hyperstition', path: '/hiperstision' },
    patterns: {
      es: [/^(?:ir|ve|navega|abrir|abre|mostrar|muestra)\s+(?:a\s+)?(?:la\s+)?hipersticion$/],
      en: [/^(?:go to|open|show)\s+(?:the\s+)?hyperstition$/]
    }
  },
  {
    action: { intent: VOICE_INTENTS.MAP, target: 'mutant_box', path: '/', localAction: 'generate_mutation' },
    patterns: {
      es: [/^(?:generar|genera)\s+(?:una\s+)?mutacion$/],
      en: [/^generate\s+(?:a\s+)?mutation$/]
    }
  },
  {
    action: { intent: VOICE_INTENTS.MAP, target: 'mutant_box', path: '/', localAction: 'scroll_to_mutant_box' },
    patterns: {
      es: [/^(?:mostrar|muestra|abrir|abre|ir|ve)\s+(?:a\s+)?(?:la\s+)?caja\s+mutante$/],
      en: [/^(?:show|open|go to)\s+(?:the\s+)?mutant\s+box$/]
    }
  },
  {
    action: { intent: VOICE_INTENTS.MAP, target: 'questionnaire', path: '/questionnaire' },
    patterns: {
      es: [/^(?:iniciar|inicia|abrir|abre|empezar|empieza)\s+(?:el\s+)?cuestionario$/],
      en: [/^(?:start|open)\s+(?:the\s+)?questionnaire$/]
    }
  }
];

export function matchVoicePattern(text, lang = getCurrentLanguage()) {
  const normalizedLang = normalizeLanguage(lang);
  const languageOrder = normalizedLang === 'en' ? ['en', 'es'] : ['es', 'en'];

  for (const currentLang of languageOrder) {
    for (const entry of VOICE_COMMAND_PATTERNS) {
      if (matchFirst(text, entry.patterns[currentLang] || [])) {
        return entry.action;
      }
    }
  }

  return null;
}

function buildHandledCommand(rawText, normalized, action) {
  return {
    ...action,
    rawText,
    normalized,
    handled: true,
  };
}

export function analyzeVoiceCommand(rawText = '', lang = getCurrentLanguage()) {
  const normalized = normalizeVoiceText(rawText);
  if (!normalized) {
    return { intent: VOICE_INTENTS.UNKNOWN, rawText, normalized, handled: false };
  }

  const localAction = matchVoicePattern(normalized, lang);
  if (localAction) {
    return buildHandledCommand(rawText, normalized, localAction);
  }

  const uiMatch = matchFirst(normalized, [
    /^(?:cerrar|cierra)\s+(?:el\s+)?chat$/,
    /^(?:mostrar|muestra|abrir|abre|desplegar|despliega)\s+(?:el\s+)?manifiesto$/,
  ]);

  if (uiMatch) {
    let action = 'UNKNOWN_UI_ACTION';
    if (/cerrar\s+(?:el\s+)?chat/.test(normalized)) action = 'CLOSE_CHAT';
    if (/manifiesto/.test(normalized)) action = 'SHOW_MANIFESTO';
    return { intent: VOICE_INTENTS.UI_ACTION, rawText, normalized, action, handled: true };
  }

  const sendToQophMatch = matchFirst(normalized, [
    /^(?:dile|di|preguntale|consulta(?:le)?|envia(?:le)?)\s+a\s+qoph\s+(.+)$/,
  ]);

  if (sendToQophMatch?.[1]) {
    return {
      intent: VOICE_INTENTS.SEND_VOICE_MESSAGE,
      rawText,
      normalized,
      targetType: 'qoph',
      message: sanitizeVoicePayload(sendToQophMatch[1]),
      handled: true,
    };
  }

  const sendToGroupMatch = matchFirst(normalized, [
    /^(?:envia|enviar|dile|di)\s+al\s+grupo\s+(.+?)\s+que\s+(.+)$/,
    /^(?:envia|enviar)\s+(?:a\s+)?grupo\s+(.+?)\s+(.+)$/,
  ]);

  if (sendToGroupMatch?.[1] && sendToGroupMatch?.[2]) {
    return {
      intent: VOICE_INTENTS.SEND_VOICE_MESSAGE,
      rawText,
      normalized,
      targetType: 'group',
      targetName: sanitizeVoicePayload(sendToGroupMatch[1], 80),
      message: sanitizeVoicePayload(sendToGroupMatch[2]),
      handled: true,
    };
  }

  const selectMatch = matchFirst(normalized, [
    /^(?:seleccionar|selecciona|abrir|abre)\s+grupo\s+(.+)$/,
    /^(?:chatear|chatea|hablar|habla)\s+con\s+(.+)$/,
  ]);

  if (selectMatch?.[1]) {
    return {
      intent: VOICE_INTENTS.SELECT_TARGET,
      rawText,
      normalized,
      targetType: 'group',
      targetName: sanitizeVoicePayload(selectMatch[1], 80),
      handled: true,
    };
  }

  const readMatch = matchFirst(normalized, [
    /^(?:leeme|lee|leer)\s+(?:el\s+)?ultimo\s+mensaje$/,
    /^(?:que\s+dice\s+qoph|que\s+ha\s+dicho\s+qoph)$/,
    /^(?:leeme|lee|leer)\s+(?:el\s+)?manifiesto$/,
  ]);

  if (readMatch) {
    const target = /manifiesto/.test(normalized) ? 'manifesto' : 'last_qoph_message';
    return { intent: VOICE_INTENTS.READ_OUT_LOUD, rawText, normalized, target, handled: true };
  }

  return { intent: VOICE_INTENTS.UNKNOWN, rawText, normalized, handled: false };
}

const speakWithWebSpeech = (text, options = {}) => new Promise((resolve, reject) => {
  if (!window.speechSynthesis) {
    reject(new Error('Web Speech API no disponible'));
    return;
  }

  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = options.lang || 'es-ES';
  utterance.rate = options.rate || 1;
  utterance.pitch = options.pitch || 1;
  utterance.onend = resolve;
  utterance.onerror = reject;
  window.speechSynthesis.speak(utterance);
});

export async function playTextToSpeech(text, options = {}) {
  const cleanText = sanitizeVoicePayload(text, MAX_TTS_TEXT_LENGTH);
  if (!cleanText) return null;

  const { lang = 'es', onStart, onEnd, onError, audioElement } = options;
  let objectUrl;
  let audio;

  try {
    onStart?.('tts-svc');
    const response = await api.post('/audio/generate', { text: cleanText, lang }, { responseType: 'blob' });
    objectUrl = URL.createObjectURL(response.data);
    audio = audioElement || new Audio();
    audio.src = objectUrl;

    await new Promise((resolve, reject) => {
      audio.onended = resolve;
      audio.onerror = reject;
      const playPromise = audio.play();
      if (playPromise?.catch) playPromise.catch(reject);
    });

    return { engine: 'tts-svc' };
  } catch (error) {
    console.warn('tts-svc no disponible; usando Web Speech API.', error?.message || error);
    onError?.(error);
    onStart?.('web-speech');
    await speakWithWebSpeech(cleanText, { ...options, lang: lang === 'es' ? 'es-ES' : 'en-US' });
    return { engine: 'web-speech' };
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    onEnd?.();
  }
}

export { MAX_VOICE_MESSAGE_LENGTH, MAX_TTS_TEXT_LENGTH };
